import React, { useContext } from "react";
import { Marker, Popup } from "react-map-gl/maplibre";
import { LayoutContext } from "../../../../context/LayoutContext";
import { getAirportConfig } from "../../../../utils/common/CommonHelperFunctions";
import { MAPLIBRE_Z_INDEX } from "../../../../utils/maps/MapConstants";
import {
  getStandDisplayStatus,
  mapGateFlightInfo,
} from "../../../../utils/maps/MapHelperFunctions";
import GateToolTip from "../../../osm-maps/map-markers/gate-stand-markers/GateToolTip";
import {
  MARKER_TOOLTIP_PRIORITY,
  useMapTooltip,
} from "../../context/MapTooltipContext";
import { mapLibreStyles } from "../../map-displays/mapStyles";

interface GateMarkersProps {
  airportCode: string;
  gatesData: any[];
}

interface GateMarkerProps {
  airportCode: string;
  gate: any;
  currentTime: Date;
  timezone: string;
}

const GateMarker: React.FC<GateMarkerProps> = ({
  airportCode,
  gate,
  currentTime,
  timezone,
}) => {
  const styles = mapLibreStyles();
  const { isTooltipVisible, showTooltip, hideTooltip } = useMapTooltip(
    `stand:${airportCode}:${gate.id}`,
    MARKER_TOOLTIP_PRIORITY
  );
  const { timeZoneId: airportTimeZone } = getAirportConfig(airportCode);
  const displayStatus = getStandDisplayStatus(gate.status, gate.occupancy);
  const longitude = Number(gate.longitude);
  const latitude = Number(gate.latitude);

  if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
    return null;
  }

  return (
    <>
      <Marker
        longitude={longitude}
        latitude={latitude}
        anchor="center"
        style={{ zIndex: MAPLIBRE_Z_INDEX.MARKER }}
      >
        <button
          type="button"
          className={styles.markerButton}
          aria-label={`Stand ${gate.name}`}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
          onFocus={showTooltip}
          onBlur={hideTooltip}
        >
          <span
            className={styles.gateMarker}
            style={{ backgroundColor: displayStatus.color }}
          >
            {String(gate.id).slice(0, 3)}
          </span>
        </button>
      </Marker>
      {isTooltipVisible && (
        <Popup
          longitude={longitude}
          latitude={latitude}
          anchor="bottom"
          offset={18}
          closeButton={false}
          closeOnClick={false}
          maxWidth="none"
        >
          <GateToolTip
            gateName={gate.name}
            displayStatusLabel={displayStatus.label}
            displayStatusColor={displayStatus.color}
            reason={gate.reason}
            current={mapGateFlightInfo(
              gate.flightData?.current,
              currentTime,
              airportTimeZone,
              timezone
            )}
            next={mapGateFlightInfo(
              gate.flightData?.next,
              currentTime,
              airportTimeZone,
              timezone
            )}
          />
        </Popup>
      )}
    </>
  );
};

const GateMarkers: React.FC<GateMarkersProps> = ({
  airportCode,
  gatesData,
}) => {
  const { timezone, currentTime } = useContext(LayoutContext);

  return (
    <>
      {gatesData.map((gate) => (
        <GateMarker
          key={gate.id}
          airportCode={airportCode}
          gate={gate}
          currentTime={currentTime}
          timezone={timezone}
        />
      ))}
    </>
  );
};

export default GateMarkers;
